import React, { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import Panel from "../components/Panel";
import { Sparkles, Send } from "lucide-react";

export default function AIAdvisor() {
    const [suggestions, setSuggestions] = useState([]);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [provider, setProvider] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => { api.get("/ai/suggestions").then((r) => setSuggestions(r.data)); }, []);
    useEffect(() => { api.get("/ai/provider").then((r) => setProvider(r.data)).catch(() => {}); }, []);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const ask = async (text) => {
        if (!text.trim() || busy) return;
        const userMsg = { role: "user", content: text };
        const assistantMsg = { role: "assistant", content: "" };
        setMessages((m) => [...m, userMsg, assistantMsg]);
        setInput("");
        setBusy(true);

        try {
            const res = await fetch(`${API}/ai/chat/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: text, session_id: sessionId }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).trim();
                    if (!payload) continue;
                    try {
                        const evt = JSON.parse(payload);
                        if (evt.type === "session") setSessionId(evt.session_id);
                        else if (evt.type === "delta") {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + evt.content };
                                return copy;
                            });
                        } else if (evt.type === "error") {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { ...copy[copy.length - 1], content: `⚠️ Error: ${evt.content}` };
                                return copy;
                            });
                        }
                    } catch (e) {}
                }
            }
        } catch (e) {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: `⚠️ Connection error: ${e.message}` };
                return copy;
            });
        } finally {
            setBusy(false);
        }
    };

    const renderContent = (content) => {
        // Simple line-by-line rendering with basic formatting
        return content.split("\n").map((line, i) => {
            if (!line.trim()) return <br key={i} />;
            const isHeader = /^#{1,3}\s/.test(line);
            const isBold = /^\*\*.+\*\*/.test(line);
            const cleaned = line.replace(/^#{1,3}\s/, "").replace(/\*\*(.+?)\*\*/g, "$1");
            const cls = isHeader ? "font-display font-bold text-[var(--brand-amber)] text-sm mt-2" : isBold ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-primary)]/90";
            return <div key={i} className={`${cls} leading-relaxed`}>{cleaned}</div>;
        });
    };

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// AI CFO ADVISOR</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3">
                        <Sparkles className="text-[var(--brand-amber)]" size={26} />
                        Ask Anything, Get Signal
                    </h1>
                    <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">Powered by Claude Sonnet 4.5 · Trained on A W Constructions financial context</p>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
                <Panel testId="ai-suggestions" title="Prompts" subtitle="CFO-grade">
                    <div className="space-y-2">
                        {suggestions.map((s, i) => (
                            <button
                                key={i}
                                data-testid={`ai-suggestion-${i}`}
                                onClick={() => ask(s.prompt)}
                                disabled={busy}
                                className="w-full text-left border border-panel p-3 hover:border-[var(--brand-amber)] transition-colors disabled:opacity-50"
                            >
                                <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]">{s.title}</div>
                                <div className="text-xs text-[var(--text-primary)]/80 mt-1 line-clamp-3">{s.prompt}</div>
                            </button>
                        ))}
                    </div>
                </Panel>

                <div className="xl:col-span-3 bg-panel border border-panel flex flex-col" style={{ minHeight: "60vh" }}>
                    <div className="px-4 py-3 border-b border-panel flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.15em] font-mono-fin text-[var(--text-primary)]">Conversation</div>
                        <div className="flex items-center gap-3 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                            {provider && (
                                <span data-testid="ai-provider-badge" className="px-1.5 py-0.5 border border-panel">
                                    via {provider.provider === "emergent" ? "Emergent LLM Key" : provider.provider === "anthropic" ? "Anthropic Direct" : "Unset"}
                                </span>
                            )}
                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[var(--pos)] rounded-full blink"></span> Claude 4.5 Online</span>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.length === 0 && (
                            <div className="text-center py-16">
                                <Sparkles size={40} className="mx-auto text-[var(--brand-amber)] mb-3" />
                                <div className="font-display font-bold text-lg text-[var(--text-primary)]">Ready when you are</div>
                                <div className="text-xs font-mono-fin text-[var(--text-secondary)] uppercase tracking-wider mt-2">Pick a prompt or ask your own</div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-[85%] p-3 border ${m.role === "user" ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel bg-[var(--bg-base)]"}`}>
                                    <div className="text-[9px] font-mono-fin uppercase tracking-[0.15em] mb-2 opacity-70">
                                        {m.role === "user" ? "// You" : "// AI CFO Advisor"}
                                    </div>
                                    <div className="text-xs md:text-sm font-body whitespace-pre-wrap">
                                        {m.role === "assistant" ? renderContent(m.content || "▊") : m.content}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                    </div>
                    <div className="border-t border-panel p-3 flex gap-2">
                        <input
                            data-testid="ai-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && ask(input)}
                            disabled={busy}
                            placeholder="Ask about cash flow, projects, debt, growth strategy..."
                            className="flex-1 bg-[var(--bg-base)] border border-panel px-3 py-2.5 text-sm font-body focus:border-[var(--brand-amber)] focus:outline-none disabled:opacity-50"
                        />
                        <button
                            data-testid="ai-send"
                            onClick={() => ask(input)}
                            disabled={busy || !input.trim()}
                            className="bg-[var(--brand-amber)] text-black px-4 font-mono-fin uppercase text-xs tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            <Send size={14} /> {busy ? "Streaming..." : "Send"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
